﻿import * as THREE from 'three/webgpu';
import Railcar from './Railcar';
import { IS_DEBUG_ENABLED, loadGLTFModel, setToonMaterial } from './util';
import Machine from './Machine';

const FRICTION = 0.995;
const FORWARD = 'forward';
const BACKWARD = 'backward';
const FORWARD_KEYS = ['ArrowUp', 'z', 'Z'];
const BACKWARD_KEYS = ['ArrowDown', 's', 'S'];

const LEFT = 'left';
const RIGHT = 'right';
const LEFT_KEYS = ['ArrowLeft', 'q', 'Q'];
const RIGHT_KEYS = ['ArrowRight', 'd', 'D'];

export default class Locomotive extends Machine {
    railcars: Railcar[] = [];

    private lastThrottleTime: number = 0;
    private heading: number = 0;
    private readonly travelDirection = new THREE.Vector3();
    rotate: number = -1; // Between -1 and 1, where -1 is full left and 1 is full right
    reverseDistance: number = 0; // Distance traveled in reverse, used for snapping back to track

    keyDownZ: typeof FORWARD | typeof BACKWARD | 'none' = 'none';
    keyDownX: typeof LEFT | typeof RIGHT | 'none' = 'none';

    constructor(scene: THREE.Scene) {
        super(scene);

        this.initEvents();
    }

    public async create() {
        if (!IS_DEBUG_ENABLED) {
            this.mesh = await loadGLTFModel('./locomotive.glb');
            this.mesh.children.forEach((group: THREE.Object3D) => {
                group.traverse((child) => {
                    const mesh = child as THREE.Mesh;
                    if (mesh.isMesh) {
                        const materials = Array.isArray(mesh.material)
                            ? mesh.material
                            : [mesh.material];

                        materials.forEach((material) => {
                            if (
                                material.name === 'emissive' &&
                                'emissive' in material
                            ) {
                                const emissiveMaterial =
                                    material as THREE.MeshStandardMaterial;
                                emissiveMaterial.emissive.set(0xffffba);
                            }

                            if (material.name === 'train') {
                                setToonMaterial(material, mesh);
                            }
                            if (material.name === 'axle') {
                                setToonMaterial(material, mesh);
                            }
                        });
                    }
                });
            });
            this.mesh.rotation.y = Math.PI;
        }

        if (IS_DEBUG_ENABLED) {
            this.mesh = new THREE.Mesh(
                new THREE.BoxGeometry(1, 1, 2),
                new THREE.MeshBasicMaterial({ color: 0xff0000 })
            );
        }

        const locomotiveLength = this.getLength();
        this.progress -= locomotiveLength / 2;

        this.scene.add(this.mesh);
    }

    private initEvents() {
        window.addEventListener('keydown', (event) => {
            if (FORWARD_KEYS.includes(event.key)) {
                this.keyDownZ = FORWARD;
            } else if (BACKWARD_KEYS.includes(event.key)) {
                this.keyDownZ = BACKWARD;
            } else if (LEFT_KEYS.includes(event.key)) {
                this.keyDownX = LEFT;
            } else if (RIGHT_KEYS.includes(event.key)) {
                this.keyDownX = RIGHT;
            }
        });

        window.addEventListener('keyup', (event) => {
            if (
                FORWARD_KEYS.includes(event.key) ||
                BACKWARD_KEYS.includes(event.key)
            ) {
                this.keyDownZ = 'none';
            }
            if (
                LEFT_KEYS.includes(event.key) ||
                RIGHT_KEYS.includes(event.key)
            ) {
                this.keyDownX = 'none';
            }
        });

        const forwardButton = document.getElementById('btn-forward');
        const backwardButton = document.getElementById('btn-backward');

        forwardButton?.addEventListener('mousedown', () => {
            this.keyDownZ = FORWARD;
        });
        forwardButton?.addEventListener('mouseup', () => {
            this.keyDownZ = 'none';
        });
        backwardButton?.addEventListener('mousedown', () => {
            this.keyDownZ = BACKWARD;
        });
        backwardButton?.addEventListener('mouseup', () => {
            this.keyDownZ = 'none';
        });

        forwardButton?.addEventListener('touchstart', (e) => {
            e.preventDefault();
            this.keyDownZ = FORWARD;
        });
        forwardButton?.addEventListener('touchend', (e) => {
            e.preventDefault();
            this.keyDownZ = 'none';
        });
        backwardButton?.addEventListener('touchstart', (e) => {
            e.preventDefault();
            this.keyDownZ = BACKWARD;
        });
        backwardButton?.addEventListener('touchend', (e) => {
            e.preventDefault();
            this.keyDownZ = 'none';
        });
    }

    private throttleAcceleration(delta: number) {
        const now = performance.now();
        if (now - this.lastThrottleTime >= 500) {
            this.acceleration += delta;
            this.acceleration = Math.max(-1, Math.min(1, this.acceleration));
            this.lastThrottleTime = now;
        }
    }

    public attachRailcar(car: Railcar) {
        this.railcars.push(car);
    }

    public animate() {
        if (this.keyDownZ === FORWARD) {
            this.throttleAcceleration(0.01);
        } else if (this.keyDownZ === BACKWARD) {
            this.throttleAcceleration(-0.01);
        } else {
            this.acceleration = 0;
            this.speed *= FRICTION;
            if (Math.abs(this.speed) < 0.001) this.speed = 0;
        }

        if (this.keyDownX === LEFT) {
            this.rotate -= 0.01;
        } else if (this.keyDownX === RIGHT) {
            this.rotate += 0.01;
        }
        if (Math.abs(this.rotate) < 0.01) this.rotate = 0;
        this.rotate = Math.max(-1, Math.min(1, this.rotate));

        this.speed += this.acceleration * 0.1;
        this.speed = Math.max(-5, Math.min(5, this.speed));

        const totalResistance = this.railcars.reduce(
            (sum, car) => sum + car.resistanceForce,
            0
        );
        this.speed *= 1 - totalResistance;

        if (IS_DEBUG_ENABLED) {
            if (this.speed !== 0 && this.rotate !== 0) {
                this.heading -= this.rotate * this.speed * 0.01;
            }

            this.mesh.rotation.y = this.heading;
            this.travelDirection.set(
                Math.sin(this.heading),
                0,
                Math.cos(this.heading)
            );

            this.mesh.position.x += this.travelDirection.x * this.speed * 0.1;
            this.mesh.position.z += this.travelDirection.z * this.speed * 0.1;
        }

        this.progress += this.speed * 0.1;

        if (!IS_DEBUG_ENABLED) {
            this.mesh.position.z = this.progress;
        }
    }
}
